import { transition } from '@helpers/animate';
import brand from '@helpers/brand';
import { fluid } from '@helpers/fluid';
import { ButtonReset } from '@helpers/global';
import { Device, from, until } from '@helpers/media';
import { default as SvgBack, default as SvgChevron } from '@img/icons/chevron-right.svg';
import ButtonStyles from '@stories/Components/Global/Buttons/Button/Button.styles';
import { rgba } from 'polished';
import styled from 'styled-components';
const Container = styled.nav `
  position: fixed;
  top: 63px;
  left: 0;
  bottom: 0;
  display: flex;
  flex-direction: column;
  background: var(--bgColor);
  color: var(--fgColor);
  /* height: calc(100vh - 63px); */
  opacity: 0;
  overflow-y: auto;
  padding: 0 0 30px;
  pointer-events: none;
  transition: opacity 0.15s;
  width: 100%;
  z-index: 100;

  @media ${from(Device.TabletLarge)} {
    display: none;
  }

  &[data-m-active='true'] {
    opacity: 1;
    pointer-events: all;
  }
`;
const TopArea = styled.div `
  background-color: var(--backgroundColor);
  border-bottom: 1px solid var(--separatorColor);
  padding: ${fluid(12, 36)} 20px;
  display: flex;
  justify-content: space-between;
  align-items: center;
`;
const ActionWrapper = styled.div `
  grid-column: 3 / span 2;
  display: flex;
  justify-content: flex-end;
  align-items: center;
  gap: ${fluid(18, 36)};

  @media ${from(Device.Tablet)} {
    grid-column: 4 / span 5;
  }

  @media ${from(Device.Desktop)} {
    grid-column: 5 / span 8;
  }

  > ${ButtonStyles.Container} {
    order: 3;
    white-space: nowrap;

    @media ${until(Device.TabletLarge)} {
      display: none;
    }
  }
`;
const CloseButton = styled.button `
  ${ButtonReset};

  font-weight: 700;
  font-size: 10px;
  text-transform: uppercase;
  color: var(--textColor);
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;

  > svg {
    display: block;
    height: 16px;
    width: 16px;
  }

  ${ActionWrapper}[data-action-open='false'] & {
    display: none;
  }

  @media ${from(Device.TabletLarge)} {
    display: none;
  }
`;
const ListArea = styled.div `
  display: flex;
  flex-direction: column;
  height: 100%;
`;
const List = styled.ul `
  padding: 0;
  flex: 0 1 auto;
  overflow: auto;
`;
const ListChildren = styled(List) `
  flex: 1 1 auto;
`;
const Item = styled.li `
  border-bottom: 1px solid ${brand.highlight.primary};
`;
const Arrow = styled(SvgChevron) `
  margin-left: auto;
  margin-right: 0;
  height: 15px;
`;
const Link = styled.a `
  display: flex;
  align-items: center;
  color: currentColor;
  background-color: ${rgba(brand.global.white, 0.05)};
  font-weight: 700;
  font-size: 16px;
  letter-spacing: -0.01em;
  line-height: 1em;
  text-decoration: none;
  padding: 16px 20px;
  border-radius: 4px;
  transition: color 0.3s, background-color 0.3s;
  position: relative;

  &:after {
    content: '';
    position: absolute;
    display: block;
    left: -18px;
    height: 24px;
    width: 4px;
    border-radius: 400000px;
    background-color: transparent;
  }

  &[data-active-page='true'] {
    color: ${brand.accent.primary};
    background-color: ${brand.global.white};

    &:after {
      background-color: ${brand.global.white};
    }
  }

  svg {
    margin-right: 10px;
  }

  ${Arrow} {
    margin-right: 0;
  }

  &:focus,
  &:hover {
    color: ${brand.accent.secondary};
    background-color: ${brand.global.white};
    text-decoration: none;
  }
`;
const Text = styled.span `
  svg {
    margin-right: 5px;
  }

  svg,
  span {
    vertical-align: middle;
  }
`;
const BackSvg = styled(SvgBack) `
  margin-left: auto;
  height: 15px;
`;
const Header = styled.header `
  border-bottom: 1px solid ${brand.highlight.primary};
  background-color: ${brand.highlight.primary};
`;
const Back = styled.button `
  ${ButtonReset};
  display: flex;
  align-items: center;
  font-weight: 700;
  color: var(--fg);
  font-size: 16px;
  line-height: 20px;
  margin: 0;
  padding: 15px 20px;

  span {
    position: relative;
  }

  > svg {
    margin-right: 10px;
    transform: scaleX(-1);
  }
`;
const Current = styled(Link) `
  color: ${brand.global.white};
  text-decoration: none;
  margin-bottom: 10px;
`;
const SmallList = styled(List) `
  padding: 5px 0;
`;
const SmallLink = styled(Link) `
  color: ${brand.highlight.primary};
  font-weight: 400;
  font-size: 16px;
`;
const LogOutBtn = styled.button `
  display: flex;
  align-items: center;
  color: ${brand.highlight.primary};
  border: none;
  background-color: ${rgba(brand.global.white, 0.05)};
  font-weight: 400;
  font-size: 16px;
  letter-spacing: -0.01em;
  line-height: 1em;
  text-decoration: none;
  padding: 14px 10px;
  border-radius: 4px;
  transition: color 0.3s, background-color 0.3s;
  position: relative;
  width: 100%;

  &:after {
    content: '';
    position: absolute;
    display: block;
    left: -18px;
    height: 24px;
    width: 4px;
    border-radius: 400000px;
    background-color: transparent;
  }

  svg {
    margin-right: 10px;
  }

  &:focus,
  &:hover {
    color: ${brand.accent.secondary};
    background-color: ${brand.global.white};
    text-decoration: none;
  }
`;
const Divider = styled.hr `
  display: block;
  border: 0;
  border-bottom: 1px solid ${brand.highlight.primary};
  width: 100%;
`;
const HomeLink = styled.a ``;
const NavLogo = styled.div ``;
const LogoMedia = styled.img `
  display: block;
  max-height: ${fluid(40, 52)};
`;
const BaseList = styled(List) `
  display: flex;
  flex: 0 0 auto;
  align-items: flex-end;
  flex-direction: column;
  height: auto;

  ${Item} {
    display: block;
    width: 100%;
  }
`;
const AccountBtnHolder = styled.div `
  grid-column: 12;

  @media ${from(Device.TabletLarge)} {
    display: none;
  }
`;
const AccountBtn = styled.a `
  background-color: transparent;
  display: block;
  border: 2px solid ${brand.global.white};
  border-radius: 9000000px;
  width: 40px;
  height: 40px;
  padding: 3px;
  text-decoration: none;
`;
const AccountBtnInner = styled.div `
  font-weight: 700;
  font-size: 14px;
  color: ${brand.accent.primary};
  border-radius: 9000000px;
  width: 30px;
  height: 30px;
  background-color: ${brand.global.white};
  display: flex;
  justify-content: center;
  align-items: center;
`;
const FooterArea = styled.div `
  padding: 12px;
  
`;
const FooterInner = styled.div `
  display: flex;
  flex-direction: column;
  gap: 12px;

  ${ButtonStyles.Container} {
    --buttonSize: 38px;
  }

  ${ButtonStyles.IconWrapper} {
    display: none;
  }
  
`;
const ContactArea = styled.div `
  display: grid;
  grid-template-columns: 1fr;
  align-items: center;
`;
const PhoneLink = styled.a `
  ${transition('opacity')};

  display: inline-flex;
  align-items: center;
  gap: 5px;
  color: var(--textColor);
  text-decoration: none;
  margin: auto;
  order: 1;

  &.focus-ring {
    outline: auto;
    outline-offset: 3px;
    outline-color: var(--textColor);
  }

  &:hover,
  &.focus-ring {
    opacity: 0.7;
  }

  > svg {
    display: block;
    height: 25px;
    width: 25px;
  }

  @media ${from(Device.TabletLarge)} {
    order: 2;
  }

  ${ActionWrapper}[data-action-open='true'] & {
    display: none;
  }
`;
const PhoneText = styled.span `
  font-weight: 400;
  font-size: 16px;
  line-height: 22px;
  white-space: nowrap;
`;
const PhoneNumber = styled.span `
  font-weight: 700;
  font-size: 18px;
  line-height: 28px;
  white-space: nowrap;

  span {
    text-decoration: underline;
  }
`;
const LoginBtn = styled.a `
  ${transition('background-size, color', 0.4)};
  border: 1px solid var(--accentColorSecondary);
  padding: 6px 23px;
  background-color: transparent;
  text-transform: uppercase;
  font-size: 16px;
  line-height: 22px;
  font-weight: 400;
  border-radius: 4px;
  color: var(--accentColorSecondary);
  text-decoration: none;
  text-align: center;

  background-image: linear-gradient(
    -67.65deg,
    transparent 50%,
    var(--accentColorPrimary) 50%
  );
  background-repeat: no-repeat;
  background-size: 0% 100%;

  &:focus {
    outline: none;
  }

  &.focus-ring {
    outline: auto;
    outline-offset: 3px;
    outline-color: var(--textColor);
  }

  &:hover,
  &.focus-ring {
    background-size: 225% 100%;
    color: ${brand.global.white};
  }
`;
const MobileNavigationStyles = {
    AccountBtn,
    AccountBtnHolder,
    AccountBtnInner,
    Arrow,
    Back,
    BackSvg,
    BaseList,
    CloseButton,
    ContactArea,
    Container,
    Current,
    Divider,
    FooterArea,
    FooterInner,
    Header,
    HomeLink,
    Item,
    Link,
    List,
    ListArea,
    ListChildren,
    LoginBtn,
    LogoMedia,
    LogOutBtn,
    NavLogo,
    PhoneLink,
    PhoneNumber,
    PhoneText,
    SmallLink,
    SmallList,
    Text,
    TopArea,
};
export default MobileNavigationStyles;
